import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "src/utils/cn";
import { Button } from "src/components/ui/button";
import { useSidebar } from "src/contexts/SidebarContext";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";

function matchesPath(currentPath, targetPath) {
  if (currentPath === targetPath) return true;
  if (targetPath === "/") return false;

  return currentPath.startsWith(`${targetPath}/`);
}

/* ── Badge ─────────────────────────────────────────────────────────────────── */
function NavBadge({ label, color = "gray" }) {
  return (
    <span
      className={cn(
        "ml-1 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wider",
        color === "blue"
          ? "bg-blue-100 text-blue-700"
          : "bg-slate-200 text-slate-500"
      )}
    >
      {label}
    </span>
  );
}

/* ── Tooltip wrapper — only active when sidebar is collapsed ──────────────── */
function NavTooltip({ label, children }) {
  const { collapsed } = useSidebar();
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/* ── Single nav item ────────────────────────────────────────────────────────── */
export function NavItem({ item, depth = 0 }) {
  const location = useLocation();
  const { collapsed } = useSidebar();
  const isActive = matchesPath(location.pathname, item.href);
  const Icon = item.icon;

  return (
    <NavTooltip label={item.label}>
      <Link
        to={item.href}
        className={cn(
          "flex h-9 items-center gap-2 self-stretch rounded-xl px-2.5 text-[13px] transition-colors duration-150 shrink-0",
          depth === 1 && "ml-4 h-8 text-[13px]",
          isActive
            ? "bg-slate-100 font-semibold text-slate-900 ring-1 ring-slate-300"
            : "font-medium text-[#23233f] hover:bg-white"
        )}
      >
        {Icon && (
          <Icon
            className={cn(
              "h-4 w-4 shrink-0",
              isActive ? "text-slate-700" : "text-[#24243a]"
            )}
          />
        )}

        {/* Text — smoothly hides when collapsed */}
        <div
          className={cn(
            "flex min-w-0 flex-1 items-center overflow-hidden transition-all duration-200",
            collapsed ? "w-0 opacity-0" : "w-auto opacity-100"
          )}
        >
          <span className="grow truncate">{item.label}</span>
          {item.badge && <NavBadge label={item.badge} color={item.badgeColor} />}
        </div>
      </Link>
    </NavTooltip>
  );
}

/* ── Collapsible nav item ───────────────────────────────────────────────────── */
function NavItemCollapsible({ item }) {
  const location = useLocation();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const isChildActive = item.children?.some((child) => matchesPath(location.pathname, child.href));
  const isActive = matchesPath(location.pathname, item.href) || isChildActive;
  const [open, setOpen] = useState(isChildActive);
  const Icon = item.icon;

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  return (
    <div>
      <NavTooltip label={item.label}>
        <Button
          variant="ghost"
          onClick={() => !sidebarCollapsed && setOpen((v) => !v)}
          className={cn(
            "flex h-10 w-full items-center gap-2 self-stretch rounded-xl px-2.5 text-left text-[14px] transition-colors duration-150 shrink-0",
            isActive
              ? "bg-slate-100 font-semibold text-slate-900 ring-1 ring-slate-300"
              : "font-medium text-[#23233f] hover:bg-white"
          )}
        >
          {Icon && (
            <Icon
              className={cn(
                "h-4 w-4 shrink-0",
                isActive ? "text-slate-700" : "text-[#24243a]"
              )}
            />
          )}

          <div
            className={cn(
              "flex min-w-0 flex-1 items-center overflow-hidden transition-all duration-200",
              sidebarCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
            )}
          >
            <span className="grow truncate">{item.label}</span>
            {item.badge && (
              <NavBadge label={item.badge} color={item.badgeColor} />
            )}
            {open ? (
              <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 text-slate-500" />
            ) : (
              <ChevronRight className="ml-1 h-3.5 w-3.5 shrink-0 text-slate-500" />
            )}
          </div>
        </Button>
      </NavTooltip>

      {/* Sub-items */}
      {open && !sidebarCollapsed && (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <NavItem key={child.id} item={child} depth={1} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Nav list (no section labels — clean flat list like Rootly) ─────────────── */
export function SidebarNav({ items }) {
  return (
    <nav className="mt-3 flex flex-1 shrink-0 flex-col gap-0.5 overflow-y-auto px-1 pb-2 pt-0.5">
      {items.map((item) => {
        if (item.children?.length) {
          return <NavItemCollapsible key={item.id} item={item} />;
        }
        return <NavItem key={item.id} item={item} />;
      })}
    </nav>
  );
}

